import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Users, MessageCircle, ThumbsUp, MapPin, Clock, Camera,
  Send, ChevronDown, ChevronUp, AlertTriangle, Cloud,
  CloudRain, Sun, Snowflake, Wind, Share2, Flag
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_TYPES = [
  { id: "rain", name: "Rain", icon: CloudRain, color: "text-blue-400" },
  { id: "snow", name: "Snow", icon: Snowflake, color: "text-white" },
  { id: "sunny", name: "Sunny", icon: Sun, color: "text-yellow-400" },
  { id: "wind", name: "Strong Wind", icon: Wind, color: "text-gray-400" },
  { id: "storm", name: "Storm", icon: AlertTriangle, color: "text-yellow-500" },
  { id: "clouds", name: "Cloudy", icon: Cloud, color: "text-gray-300" },
];

export default function WeatherCommunity({ token, location, user, isDarkMode, compact = false }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewReport, setShowNewReport] = useState(false);
  const [newReport, setNewReport] = useState({
    type: "rain",
    description: "",
    intensity: "moderate"
  });
  const [filter, setFilter] = useState("nearby"); // nearby, recent, popular

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        lat: location?.lat,
        lon: location?.lon,
        filter
      };
      const response = await axios.get(`${API}/community/reports`, { params });
      setReports(response.data.reports || []);
    } catch (error) {
      console.error("Failed to fetch community reports:", error);
    } finally {
      setLoading(false);
    }
  }, [location, filter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const submitReport = async () => {
    if (!newReport.description) {
      toast.error("Please add a description");
      return;
    }

    try {
      const response = await axios.post(`${API}/community/reports`, {
        ...newReport,
        lat: location?.lat,
        lon: location?.lon,
        location_name: location?.name || "Unknown"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReports([response.data.report, ...reports]);
      setShowNewReport(false);
      setNewReport({ type: "rain", description: "", intensity: "moderate" });
      toast.success("Weather report submitted!");
    } catch (error) {
      toast.error("Failed to submit report");
    }
  };

  const likeReport = async (reportId) => {
    try {
      await axios.post(`${API}/community/reports/${reportId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(reports.map(r => 
        r.id === reportId ? { ...r, likes: (r.likes || 0) + 1, user_liked: true } : r
      ));
    } catch (error) {
      toast.error("Failed to like report");
    }
  };

  const shareReport = (report) => {
    const text = `Weather report from ${report.location_name}: ${report.description}`;
    if (navigator.share) {
      navigator.share({
        title: "Weather Report",
        text,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Report copied to clipboard!");
    }
  };

  const getReportType = (typeId) => REPORT_TYPES.find(t => t.id === typeId) || REPORT_TYPES[0];

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="weather-community-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-[#ff0080]" />
          <div>
            <h3 className="font-bold">Weather Community</h3>
            <p className="text-xs opacity-60">Nearby user reports</p>
          </div>
        </div>
        <Button
          data-testid="community-new-report-button"
          size="sm"
          onClick={() => setShowNewReport(true)}
          className="bg-gradient-to-r from-[#ff0080] to-[#7000ff] text-white hover:opacity-90 rounded-lg"
        >
          <Camera className="w-4 h-4 mr-1" />
          Report
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="community-filter-tabs">
        {[
          { id: "nearby", label: "Nearby" },
          { id: "recent", label: "Recent" },
          { id: "popular", label: "Popular" }
        ].map(tab => (
          <Button
            data-testid={`community-filter-${tab.id}-button`}
            key={tab.id}
            size="sm"
            variant={filter === tab.id ? "default" : "ghost"}
            onClick={() => setFilter(tab.id)}
            className={filter === tab.id ? "bg-[#00f2ea] text-black" : ""}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Reports List */}
      <div className={`space-y-2 overflow-y-auto ${compact ? 'max-h-56' : 'max-h-96'}`} data-testid="community-reports-list">
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className={`animate-pulse ${compact ? 'h-14' : 'h-24'} bg-white/10 rounded-xl`} data-testid={`community-loading-row-${i}`} />
            ))}
          </div>
        ) : reports.length === 0 ? (
          <div className={`rounded-xl text-center ${compact ? 'p-3' : 'p-6'} ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="community-empty-state">
            <Users className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} mx-auto mb-2 opacity-40`} />
            <p className="text-sm opacity-60">No weather reports nearby</p>
            {!compact && <p className="text-xs opacity-40 mt-1">Be the first to report the weather!</p>}
          </div>
        ) : (
          reports.slice(0, compact ? 2 : reports.length).map(report => {
            const reportType = getReportType(report.type);
            const IconComponent = reportType.icon;
            
            return (
              <motion.div
                key={report.id}
                data-testid={`community-report-${report.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${compact ? 'p-3' : 'p-4'} rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
              >
                {/* Report Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`${compact ? 'p-1.5' : 'p-2'} rounded-lg ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}>
                      <IconComponent className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${reportType.color}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{reportType.name}</p>
                      <div className="flex items-center gap-2 text-xs opacity-50">
                        <MapPin className="w-3 h-3" />
                        <span>{report.location_name}</span>
                        <Clock className="w-3 h-3 ml-1" />
                        <span>{getTimeAgo(report.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    report.intensity === 'heavy' ? 'bg-red-500/20 text-red-400' :
                    report.intensity === 'light' ? 'bg-green-500/20 text-green-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {report.intensity}
                  </span>
                </div>

                {/* Description */}
                <p className={`text-sm opacity-80 ${compact ? 'mb-2 line-clamp-2' : 'mb-3'}`}>{report.description}</p>

                {/* Reporter */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#00f2ea] to-[#7000ff] flex items-center justify-center text-xs font-bold">
                      {report.user_name?.[0] || "?"}
                    </div>
                    <span className="text-xs opacity-60">{report.user_name || "Anonymous"}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid={`community-like-${report.id}-button`}
                      variant="ghost"
                      size="sm"
                      onClick={() => likeReport(report.id)}
                      className={`text-xs ${report.user_liked ? 'text-[#ff0080]' : 'opacity-60'}`}
                    >
                      <ThumbsUp className="w-4 h-4 mr-1" />
                      {report.likes || 0}
                    </Button>
                    <Button
                      data-testid={`community-share-${report.id}-button`}
                      variant="ghost"
                      size="sm"
                      onClick={() => shareReport(report)}
                      className="text-xs opacity-60"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* New Report Modal */}
      <AnimatePresence>
        {showNewReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowNewReport(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md rounded-2xl p-6 ${
                isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-bold text-lg mb-4" data-testid="community-report-modal-title">Report Current Weather</h3>
              
              <div className="space-y-4">
                {/* Weather Type */}
                <div>
                  <label className="text-sm opacity-60 mb-2 block">What&apos;s the weather like?</label>
                  <div className="grid grid-cols-3 gap-2">
                    {REPORT_TYPES.map(type => {
                      const IconComponent = type.icon;
                      return (
                        <button
                          data-testid={`community-report-type-${type.id}-button`}
                          key={type.id}
                          onClick={() => setNewReport({ ...newReport, type: type.id })}
                          className={`p-3 rounded-xl text-center transition-all ${
                            newReport.type === type.id
                              ? 'bg-[#00f2ea]/20 border-2 border-[#00f2ea]'
                              : isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'
                          }`}
                        >
                          <IconComponent className={`w-6 h-6 mx-auto mb-1 ${type.color}`} />
                          <span className="text-xs">{type.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Intensity */}
                <div>
                  <label className="text-sm opacity-60 mb-2 block">Intensity</label>
                  <div className="flex gap-2">
                    {["light", "moderate", "heavy"].map(intensity => (
                      <Button
                        data-testid={`community-intensity-${intensity}-button`}
                        key={intensity}
                        size="sm"
                        variant={newReport.intensity === intensity ? "default" : "ghost"}
                        onClick={() => setNewReport({ ...newReport, intensity })}
                        className={newReport.intensity === intensity ? "bg-[#00f2ea] text-black" : ""}
                      >
                        {intensity.charAt(0).toUpperCase() + intensity.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm opacity-60 mb-1 block">Description</label>
                  <Input
                    data-testid="community-report-description-input"
                    placeholder="Describe what you're seeing..."
                    value={newReport.description}
                    onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                    className={isDarkMode ? 'bg-white/5' : 'bg-black/5'}
                  />
                </div>

                {/* Location */}
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-[#00f2ea]" />
                    <span>{location?.name || "Current Location"}</span>
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button data-testid="community-cancel-report-button" variant="ghost" onClick={() => setShowNewReport(false)}>
                    Cancel
                  </Button>
                  <Button
                    data-testid="community-submit-report-button"
                    onClick={submitReport}
                    className="bg-gradient-to-r from-[#ff0080] to-[#7000ff] text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit Report
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
